import React from 'react';
import {
  Breakpoints,
  ButtonAppearance,
  ButtonCustom,
  buttonPaddingGenerator,
  CustomImage,
  Link,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  useIsMobile,
} from '@vw-marketing/us-components';
import {useTranslations} from '../../hooks/use-translations';
import {Dealer, UsedVehicle} from '../../typing/used-inventory';
import {
  AppConstants,
  GOOGLE_MAPS_DESTINATION_URL,
} from '../../utils/app-constants';
import {stringToPhoneFormat} from '../../utils/general';
import CustomerCenter from '../icons/customer-center';
import External from '../icons/external';
import PinPlace from '../icons/pin-place';
import {
  dealerDetailMobileTextStyle,
  dealerDetailTextStyle,
  dealerDistanceTextStyle,
  dealerNameTextStyle,
  dealerSiteTextStyle,
} from './helper';
import {
  StyledMapsImage,
  StyledButton,
  StyledDealer,
  StyledDealerName,
  StyledDealerContact,
  StyledDealerLocation,
  StyledDealerNameMiles,
  StyledDealerContactPhoneSite,
  StyledDealerSite,
  StyledInfoWrapper,
} from './styles';
import {useTrackingManager} from '../../hooks/use-tracking-manager';
import {onVWBasicDealerLinkClick} from '../../tagging/tagging-helpers';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {modalsData} from '../../store/local-storage';
import {ModalDisplayMode} from '../../typing/modal-display';

interface DealerDetailsProps {
  vehicle: UsedVehicle;
  isPrimary?: boolean;
  onHandleContactDealer: () => void;
  children?: React.ReactNode;
}

const DealerDetails: React.FC<DealerDetailsProps> = props => {
  const {
    vehicle: {dealer},
    isPrimary,
    children,
    onHandleContactDealer,
  } = props;
  if (!dealer) return <></>;

  const translations = useTranslations().dealerDetails;
  const isMobile = useIsMobile(Breakpoints.b560);
  const trackinManager = useTrackingManager();
  const {faHostServerUrl = ''} = useFeatureAppConfig();

  const onHandleDealerRedirect = () => {
    modalsData({
      vehicle: props.vehicle,
      active: true,
      mode: ModalDisplayMode.DEALER_SITE_HANDOFF,
      urlRedirect: dealer?.url,
    });
    trackinManager &&
      onVWBasicDealerLinkClick(
        {
          name: translations.visitDealerSiteLabel,
          url: dealer?.url,
        },
        trackinManager,
        dealer,
      );
  };

  const openMapHandler = (dealer?: Dealer) => {
    if (!dealer) return;

    const dealerDirection =
      GOOGLE_MAPS_DESTINATION_URL +
      encodeURIComponent(
        `${dealer.address1} ${dealer.city} ${dealer.state} ${dealer.postalcode}`,
      );
    open(dealerDirection, '_blank');
  };

  return (
    <StyledDealer className="details-dealer">
      {children && <div className="details-dealer-header">{children}</div>}
      <StyledMapsImage
        className="details-maps-image"
        onClick={openMapHandler.bind(null, dealer)}
      >
        <CustomImage
          alt={dealer?.name || ''}
          srcPlaceholder={AppConstants.PlaceHolder480x216 as string}
          srcErrorImage={
            (faHostServerUrl + AppConstants.Ngw6Logo480x216) as string
          }
          src={dealer?.staticMapsUrl || ''}
        />
      </StyledMapsImage>
      <StyledInfoWrapper className="details-dealer-info-wrapper">
        <StyledDealerNameMiles className="details-dealer-name-miles">
          <StyledDealerName className="details-dealer-name notranslate">
            <TextCustom {...dealerNameTextStyle}>{dealer?.name}</TextCustom>
          </StyledDealerName>
          <TextCustom {...dealerDistanceTextStyle}>
            ({dealer?.distance.toFixed(1)} {translations.miles})
          </TextCustom>
        </StyledDealerNameMiles>

        <StyledDealerLocation className="details-dealer-location notranslate">
          <PinPlace iconSize={16} />
          <TextCustom {...dealerDetailTextStyle}>{dealer?.address1}</TextCustom>
          <TextCustom {...dealerDetailTextStyle}>
            {dealer?.city}, {dealer?.state} {dealer?.postalcode}
          </TextCustom>
        </StyledDealerLocation>
        <StyledDealerContactPhoneSite className="details-dealer-phone-site">
          {isMobile ? (
            <Link href={`tel:${dealer?.phone}`} isInline>
              <StyledDealerContact className="details-dealer-phone notranslate">
                <CustomerCenter iconSize={'16'} />
                <TextCustom {...dealerDetailMobileTextStyle}>
                  {stringToPhoneFormat(dealer?.phone || '')}
                </TextCustom>
              </StyledDealerContact>
            </Link>
          ) : (
            <StyledDealerContact className="details-dealer-phone notranslate">
              <CustomerCenter iconSize={'16'} />
              <TextCustom {...dealerDetailTextStyle}>
                {stringToPhoneFormat(dealer?.phone || '')}
              </TextCustom>
            </StyledDealerContact>
          )}
          <StyledDealerSite className="details-dealer-site">
            <Link onClick={onHandleDealerRedirect} isInline>
              <TextCustom {...dealerSiteTextStyle}>
                {translations.visitDealerSiteLabel}
              </TextCustom>
              <External iconSize={14} />
            </Link>
          </StyledDealerSite>
        </StyledDealerContactPhoneSite>
        <StyledButton className="details-dealer-contact-button">
          <ButtonCustom
            appearance={
              isPrimary ? ButtonAppearance.Primary : ButtonAppearance.Secondary
            }
            onClick={onHandleContactDealer}
            padding={buttonPaddingGenerator(12, 10, 10, 21)}
          >
            <TextCustom
              appearance={TextAppearance.copy200}
              style={{
                fontSize: TextSize.TextSize14,
                lineHeight: TextLineHeight.TextLineHeight24,
              }}
            >
              {translations.contactADealerCTA}
            </TextCustom>
          </ButtonCustom>
        </StyledButton>
      </StyledInfoWrapper>
    </StyledDealer>
  );
};

export default DealerDetails;
